import React, { useState } from 'react';
import { Add } from '@mui/icons-material';
import { Box, Button, ButtonGroup, Grow } from '@mui/material';

export interface BetweenItemAddButtonProps {
  /** Callback when button is clicked */
  onClick: () => void;
  /** When false, hides button content but preserves hover zone (useful during drag operations) */
  enabled: boolean;
  /** Button label text (default: "Section") */
  label?: string;
  /** Custom icon (default: Add icon) */
  icon?: React.ReactNode;
}

/**
 * Reusable "add item" button component with polished hover interactions
 *
 * Features:
 * - Hover line indicator
 * - Grow animation for button appearance
 * - Height transition on hover (32px → 60px)
 * - Elevated button with shadow
 * - Hides during drag operations while preserving hover zone
 *
 * Used in drag-and-drop interfaces to add items between existing items.
 *
 * @example
 * ```tsx
 * <BetweenItemAddButton
 *   enabled={!isDragging}
 *   onClick={handleAddSection}
 *   label="Section"
 * />
 * ```
 */
export const BetweenItemAddButton: React.FC<BetweenItemAddButtonProps> = ({
  onClick,
  enabled,
  label = 'Section',
  icon,
}) => {
  const [isHovered, setIsHovered] = useState(false);

  // Determine hover line color based on state
  const getHoverLineColor = () => {
    if (!enabled) return 'transparent';
    if (isHovered) return 'divider';
    return 'transparent';
  };

  const handleClick = () => {
    onClick();
    setIsHovered(false);
  };

  return (
    <Box
      sx={{
        position: 'relative',
        height: isHovered ? 60 : 32,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        transition: 'all 0.2s ease',
        cursor: 'pointer',
        my: 0,
      }}
      onMouseEnter={() => {
        setIsHovered(true);
      }}
      onMouseLeave={() => {
        setIsHovered(false);
      }}
      title={`Add ${label}`}
    >
      {/* Hover line - transparent when not enabled */}
      <Box
        sx={{
          position: 'absolute',
          left: 0,
          right: 0,
          height: 2,
          backgroundColor: getHoverLineColor(),
          transition: 'all 0.2s ease',
        }}
      />

      {/* Button - hide when not enabled */}
      {enabled && (
        <Grow in={isHovered}>
          <Box
            sx={{
              position: 'absolute',
              backgroundColor: 'background.paper',
              boxShadow: 2,
              borderRadius: 1,
              p: 0.5,
            }}
          >
            <ButtonGroup size="small" variant="outlined">
              <Button
                startIcon={icon ?? <Add />}
                onClick={handleClick}
                sx={{ minWidth: 'auto', px: 1.5 }}
              >
                {label}
              </Button>
            </ButtonGroup>
          </Box>
        </Grow>
      )}
    </Box>
  );
};
